import React, { useEffect, useState } from 'react';
import { CheckCircle2, CircleDot, Ban, Hammer, Layers } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { productionOrdersService, productsService, warehousesService } from '../../services/productionService';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import DataTable from '../../components/DataTable';
import StatCards from '../../components/StatCards';
import useTableList from '../../utils/useTableList';
import useLookup from '../../utils/useLookup';
import usePageTitle from '../../utils/usePageTitle';
import { formatQuantity } from '../../lib/utils';

function ProductionOrdersList() {
    usePageTitle('Ordens de Produção');
    const navigate = useNavigate();
    // Carrega dados iniciais
    const list = useTableList({
        storageKey: 'production-orders',
        fetcher: (params) => productionOrdersService.list(params),
    });

    const openDrawerForNew = () => {
        navigate('/production/orders/new');
    };

    const openDrawerForEdit = (order) => {
        navigate(`/production/orders/${order.id}`);
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            open: { label: 'Aberta', variant: 'default' },
            in_progress: { label: 'Em Produção', variant: 'warning' },
            finished: { label: 'Finalizada', variant: 'success' },
            cancelled: { label: 'Cancelada', variant: 'destructive' },
        };
        const config = statusMap[status] || { label: status, variant: 'default' };
        return <Badge variant={config.variant}>{config.label}</Badge>;
    };

    const formatDatePtBR = (value) => {
        if (!value) return '-';
        // API sends YYYY-MM-DD (date) for production orders
        const d = new Date(`${value}T00:00:00`);
        if (Number.isNaN(d.getTime())) return value;
        return d.toLocaleDateString('pt-BR');
    };

    const columns = [
        {
            key: 'id',
            label: 'ID',
            className: 'font-medium',
        },
        {
            key: 'product',
            label: 'Produto',
            render: (value, row) => row.product ? `${row.product.code} - ${row.product.description}` : '-',
        },
        {
            key: 'planned_quantity',
            label: 'Quantidade Planejada',
            render: (value) => formatQuantity(value),
        },
        {
            key: 'produced_quantity',
            label: 'Quantidade Produzida',
            render: (value) => formatQuantity(value),
        },
        {
            key: 'status',
            label: 'Status',
            render: (value) => getStatusBadge(value),
        },
        {
            key: 'date',
            label: 'Data',
            render: (value) => formatDatePtBR(value),
        },
    ];

    const products = useLookup(() => productsService.list({ per_page: 1000 }));
    const warehouses = useLookup(() => warehousesService.list({ per_page: 1000 }));

    const filters = [
        { key: 'number', label: 'Nº da ordem', type: 'text' },
        { key: 'product_id', label: 'Produto', type: 'autocomplete', options: products.map((p) => ({ value: Number(p.id), label: `${p.code} - ${p.description || ''}` })) },
        { key: 'status', label: 'Status', type: 'select', options: [
                { value: 'open', label: 'Aberta' },
                { value: 'in_progress', label: 'Em Produção' },
                { value: 'finished', label: 'Finalizada' },
                { value: 'cancelled', label: 'Cancelada' },
            ] },
        { key: 'target_warehouse_id', label: 'Depósito de destino', type: 'select', options: warehouses.map((w) => ({ value: String(w.id), label: w.name })) },
        { key: 'date_from', label: 'Data de', type: 'date' },
        { key: 'date_to', label: 'Data até', type: 'date' },
    ];

    const [stats, setStats] = useState({ open: 0, in_progress: 0, finished: 0, cancelled: 0 });

    useEffect(() => {
        productionOrdersService.stats()
            .then((response) => setStats((prev) => ({ ...prev, ...response.data })))
            .catch((e) => console.error('Error loading order stats:', e));
    }, []);

    const total = stats.open + stats.in_progress + stats.finished + stats.cancelled;

    const statCards = [
        { key: 'total', label: 'Todas', Icon: Layers, tone: 'bg-gray-100 text-gray-600', value: total },
        { key: 'open', value: stats.open, label: 'Abertas', Icon: CircleDot, tone: 'bg-blue-100 text-blue-600' },
        { key: 'in_progress', value: stats.in_progress, label: 'Em Produção', Icon: Hammer, tone: 'bg-amber-100 text-amber-600' },
        { key: 'finished', value: stats.finished, label: 'Finalizadas', Icon: CheckCircle2, tone: 'bg-green-100 text-green-600' },
        { key: 'cancelled', value: stats.cancelled, label: 'Canceladas', Icon: Ban, tone: 'bg-rose-100 text-rose-600' },
    ];

    return (
        <AppLayout>
            <div className="space-y-6">
                {/* Header */}
                <PageHeader
                    title="Ordens de Produção"
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Produção', path: '/production/orders' },
                    ]}
                    actions={[
                        {
                            label: 'Nova Ordem',
                            icon: 'new',
                            as: Link,
                            to: '/production/orders/new',
                        },
                    ]}
                />

                <StatCards
                    cards={statCards}
                    className="lg:grid-cols-5"
                    activeKey={list.filterValues.status || 'total'}
                    onSelect={(key) => list.load(1, list.searchValue, { ...list.filterValues, status: key === 'total' ? '' : key })}
                />

                {/* Table */}
                <DataTable
                    list={list}
                    filters={filters}
                    searchPlaceholder="Buscar por número, código ou produto..."
                    columns={columns}
                    editTo={(row) => `/production/orders/${row.id}`}
                    emptyMessage="Nenhuma ordem de produção cadastrada"
                />
            </div>
        </AppLayout>
    );
}

export default ProductionOrdersList;

